/**
 * Route Royal Footer Component
 * 
 * Simple footer for Route Royal marketing site
 */

'use client';

import Link from 'next/link';
import Image from 'next/image';
import type { FooterProps } from '@routeroyal/theme-core';

export function RouteRoyalFooter({
  companyName,
  tagline,
  contactEmail,
  contactPhone,
  address,
  socialLinks,
  legalLinks = [],
  quickLinks = [],
  logoUrl,
}: FooterProps) {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            {logoUrl && (
              <Image
                src={logoUrl}
                alt={companyName || 'Route Royal'}
                width={200}
                height={60}
                className="h-10 w-auto mb-4"
              />
            )}
            {tagline && (
              <p className="text-gray-400 mb-4">{tagline}</p>
            )}
            <div className="space-y-2">
              {contactEmail && (
                <p className="text-gray-400">
                  Email: <a href={`mailto:${contactEmail}`} className="text-white hover:underline">{contactEmail}</a>
                </p>
              )}
              {contactPhone && (
                <p className="text-gray-400">
                  Phone: <a href={`tel:${contactPhone}`} className="text-white hover:underline">{contactPhone}</a>
                </p>
              )}
              {address && (
                <p className="text-gray-400">
                  {address.street}, {address.city}
                  {address.postalCode && `, ${address.postalCode}`}
                  {address.country && `, ${address.country}`}
                </p>
              )}
            </div>
          </div>

          {/* Quick Links */}
          {quickLinks.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link href={link.href} className="text-gray-400 hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal Links */}
          {legalLinks.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                {legalLinks.map((link, index) => (
                  <li key={index}>
                    <Link href={link.href} className="text-gray-400 hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Social Links */}
        {socialLinks && (
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="flex items-center space-x-6">
              {socialLinks.linkedin && (
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </a>
              )}
              {socialLinks.twitter && (
                <a
                  href={socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Twitter"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <p>&copy; {new Date().getFullYear()} {companyName || 'Route Royal'}. All rights reserved.</p>
        </div>

        {/* Structured Data for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'RealEstateAgent',
              name: companyName || 'Route Royal',
              ...(tagline && { description: tagline }),
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                ...(contactEmail && { email: contactEmail }),
                ...(contactPhone && { telephone: contactPhone }),
              },
              ...(address && {
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: address.street,
                  addressLocality: address.city,
                  ...(address.postalCode && { postalCode: address.postalCode }),
                  ...(address.country && { addressCountry: address.country }),
                },
              }),
              sameAs: Object.values(socialLinks || {}).filter(Boolean),
            }),
          }}
        />
      </div>
    </footer>
  );
}

